/**
 * NewCardForm Component
 *
 * Wrapper component for Stripe Elements that handles SetupIntent creation
 * and card confirmation flow. Used within PaymentRequestModal when user
 * selects "Use a different card".
 *
 * Flow:
 * 1. Creates SetupIntent on mount
 * 2. Renders StripePaymentForm within Elements provider
 * 3. Exposes confirmCard() method via ref that:
 *    - Confirms the card with Stripe
 *    - Saves it to the patient's profile
 *    - Returns the payment_method_id for immediate charging
 */

import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';

import {
  StripePaymentForm,
  type StripePaymentFormHandle,
} from '@features/subscriptions/components/StripePaymentForm';

import { usePatientPaymentMethods, useStripeConnect } from '../store/hooks';

// Cache for Stripe instances by account ID (shared with AddPatientPaymentMethodModal)
const stripeInstanceCache: Record<string, Promise<Stripe | null>> = {};

/**
 * Get or create a Stripe instance for the given connected account
 */
function getStripeInstance(stripeAccountId: string | null): Promise<Stripe | null> {
  const cacheKey = stripeAccountId || 'platform';

  if (!stripeInstanceCache[cacheKey]) {
    const publishableKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string) || '';
    stripeInstanceCache[cacheKey] = loadStripe(
      publishableKey,
      stripeAccountId ? { stripeAccount: stripeAccountId } : undefined
    );
  }

  return stripeInstanceCache[cacheKey];
}

export interface NewCardFormHandle {
  /**
   * Confirm the card with Stripe and save it to the patient's profile.
   * @returns The payment_method_id that can be used for charging
   */
  confirmCard: () => Promise<string>;
}

export interface NewCardFormProps {
  /** Patient ID to save the card to */
  patientId: string;
  /** Callback when the form is ready (SetupIntent loaded, Elements ready) */
  onReady?: () => void;
  /** Callback when an error occurs during setup */
  onError?: (error: string) => void;
  /** Whether the parent is processing (disables form) */
  disabled?: boolean;
}

/**
 * Inner form component that uses Stripe hooks
 * Must be rendered inside Elements provider
 */
interface InnerFormProps {
  patientId: string;
  onReady?: () => void;
  disabled?: boolean;
}

const InnerForm = forwardRef<NewCardFormHandle, InnerFormProps>(function InnerForm(
  { patientId, onReady, disabled },
  ref
) {
  const formRef = useRef<StripePaymentFormHandle>(null);
  const { confirmNewPaymentMethod } = usePatientPaymentMethods(patientId);
  const [isConfirming, setIsConfirming] = useState(false);

  // Expose confirmCard method to parent
  useImperativeHandle(
    ref,
    () => ({
      confirmCard: async (): Promise<string> => {
        if (!formRef.current) {
          throw new Error('Payment form is not ready');
        }

        setIsConfirming(true);
        try {
          // Step 1: Confirm the SetupIntent with Stripe (saves card on Stripe's side)
          const paymentMethodId = await formRef.current.confirmPayment();

          // Step 2: Confirm with our backend (links card to patient)
          await confirmNewPaymentMethod();

          return paymentMethodId;
        } finally {
          setIsConfirming(false);
        }
      },
    }),
    [confirmNewPaymentMethod]
  );

  return (
    <StripePaymentForm
      ref={formRef}
      onElementLoaded={onReady}
      returnUrl={window.location.href}
      showProcessingIndicator={false}
      isParentProcessing={disabled || isConfirming}
      hideBillingDetails={false}
      hideWallets={true}
      hideLink
    />
  );
});

/**
 * NewCardForm - Stripe Elements wrapper for adding a new card
 *
 * Usage:
 * ```tsx
 * const formRef = useRef<NewCardFormHandle>(null);
 *
 * // When user clicks "Charge"
 * const paymentMethodId = await formRef.current.confirmCard();
 * // Use paymentMethodId to charge the card
 * ```
 */
export const NewCardForm = forwardRef<NewCardFormHandle, NewCardFormProps>(function NewCardForm(
  { patientId, onReady, onError, disabled },
  ref
) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const innerRef = useRef<NewCardFormHandle>(null);

  const { setupNewPaymentMethod } = usePatientPaymentMethods(patientId);
  const { stripeAccountId } = useStripeConnect();

  // Get Stripe instance for the connected account
  const stripePromise = useMemo(
    () => getStripeInstance(stripeAccountId),
    [stripeAccountId]
  );

  // Fetch SetupIntent on mount
  useEffect(() => {
    let cancelled = false;

    const createSetupIntent = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await setupNewPaymentMethod();
        if (!cancelled && data.client_secret) {
          setClientSecret(data.client_secret);
        }
      } catch (err) {
        if (!cancelled) {
          const errorMsg = err instanceof Error ? err.message : 'Failed to initialize payment form';
          setError(errorMsg);
          onError?.(errorMsg);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    createSetupIntent();

    return () => {
      cancelled = true;
    };
  }, [setupNewPaymentMethod, onError]);

  // Forward the ref to the inner form
  useImperativeHandle(ref, () => ({
    confirmCard: async () => {
      if (!innerRef.current) {
        throw new Error('Payment form is not ready');
      }
      return innerRef.current.confirmCard();
    },
  }));

  // Handle retry
  const handleRetry = useCallback(() => {
    setClientSecret(null);
    setError(null);
    setIsLoading(true);
    setupNewPaymentMethod()
      .then(data => {
        if (data.client_secret) {
          setClientSecret(data.client_secret);
        }
      })
      .catch(err => {
        const errorMsg = err instanceof Error ? err.message : 'Failed to initialize payment form';
        setError(errorMsg);
        onError?.(errorMsg);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [setupNewPaymentMethod, onError]);

  // Loading state
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4, gap: 2 }}>
        <CircularProgress size={24} />
        <Typography color="text.secondary">Preparing secure form...</Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Typography
            component="button"
            variant="body2"
            onClick={handleRetry}
            sx={{
              background: 'none',
              border: 'none',
              color: 'inherit',
              textDecoration: 'underline',
              cursor: 'pointer',
            }}
          >
            Retry
          </Typography>
        }
      >
        {error}
      </Alert>
    );
  }

  // Ready state - render Elements with form
  if (!clientSecret) {
    return null;
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#1976d2',
            fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
          },
        },
      }}
    >
      <InnerForm
        ref={innerRef}
        patientId={patientId}
        onReady={onReady}
        disabled={disabled}
      />
    </Elements>
  );
});

export default NewCardForm;
